import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { AdjustInputs, CloseInputs, MaxRiskCeiling, PriceRange, SelectInputs, TabInputs, TabKind } from './copilot-domain';
import { CoPilotBookHedge } from './copilot-facade';

// Collapsed "informational panel" for a tab whose questionnaire has been Run (owner 2026-06-22). It is a
// PURE PROJECTION of the SUBMITTED inputs into compact chips — it owns no state and never edits. Clicking
// it asks the parent to re-open the full form (click-to-expand). One formatter, branched per tabKind.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DOW3 = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Canonical date string, identical across every CoPilot: "Wed 08-Jul-26" (weekday + dd-MMM-yy).
function fmtDate(d: Date): string {
    if (!(d instanceof Date) || isNaN(d.getTime())) {
        return '—';
    }
    const dd = d.getDate() < 10 ? '0' + d.getDate() : '' + d.getDate();
    const yy = d.getFullYear() % 100;
    const yyStr = yy < 10 ? '0' + yy : '' + yy;
    return DOW3[d.getDay()] + ' ' + dd + '-' + MONTHS[d.getMonth()] + '-' + yyStr;
}

function fmtMoney(n: number): string {
    if (typeof n !== 'number' || !isFinite(n)) {
        return '—';
    }
    return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtStrike(n: number): string {
    return (typeof n === 'number' && isFinite(n)) ? '' + n : '—';
}

function priceRangeChip(pr: PriceRange): string {
    if (pr.kind === 'bounded') {
        // CR-20260820-002: an expected-range run has no strikes to print — the trader typed none,
        // and the two the BACKEND measured never come back to the panel. Printing the em-dashes
        // fmtStrike would produce ("Range · —–—") would read as a broken range rather than a
        // deliberate one, so the chip names the form instead.
        if (pr.useExpectedRange) {
            return 'Range · Expected Range';
        }
        return 'Range · ' + fmtStrike(pr.startStrike) + '–' + fmtStrike(pr.endStrike);
    }
    // Named separately from Range even though the strikes read identically — the collapsed panel is
    // the only place the submitted form is still visible, and these two differ in what was allowed.
    if (pr.kind === 'fillGap') {
        return 'Fill Gap · ' + fmtStrike(pr.startStrike) + '–' + fmtStrike(pr.endStrike);
    }
    if (pr.kind === 'followThrough') {
        // CR-20260824-015: per side - strike and arrow per enabled side, plus the toggle when it
        // is on. The collapsed panel is the only place the submitted form stays visible, so the
        // chip must say the whole shape: "Follow-Through · 7700 ↑ · 7640 ↓ · close remaining".
        const parts: string[] = [];
        if (pr.up != null) {
            parts.push(fmtStrike(pr.up.startStrike) + ' ↑');
        }
        if (pr.down != null) {
            parts.push(fmtStrike(pr.down.startStrike) + ' ↓');
        }
        if (pr.closeRemaining) {
            parts.push('Close Remaining');
        }
        return 'Follow-Through · ' + parts.join(' · ');
    }
    return 'Wall';
}

function minProfitChip(amount: number, normalized: boolean): string {
    return 'Min Profit · ' + fmtMoney(amount) + (normalized ? ' · Normalized' : '');
}

function maxRiskChip(mr: MaxRiskCeiling): string {
    if (mr.kind === 'dollar') {
        // The level rides the chip since CR-20260819-002: it decides WHERE the risk lands, and the
        // collapsed panel is the only place the submitted answer is still visible.
        return 'Max Risk · ' + fmtMoney(mr.amount) + (mr.normalized ? ' · Normalized' : '')
            + ' · ' + mr.level;
    }
    return 'Max Risk · ' + mr.level;
}

@Component({
    selector: 'ets-hg-copilot-input-summary',
    templateUrl: 'hg-copilot-input-summary.component.html',
    styleUrls: ['./hg-copilot-input-summary.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HgCopilotInputSummaryComponent {
    @Input() tabKind: TabKind = 'select';
    @Input() inputs: TabInputs | null = null;
    @Input() bookHedges: CoPilotBookHedge[] = [];   // Close only: resolves hedge ids -> display names

    // CR-20260831-004: the tab's changed-vs-last-run key map (hg-copilot-param-diff grammar). The
    // strip renders the LIVE inputs, so a changed chip shows the new value with the yellow "does
    // not match the results" tint. A chip collapses several diff keys — any hit under its family
    // flags it. Null/empty = clean.
    @Input() changedKeys: { [key: string]: boolean } | null = null;

    @Output() expand = new EventEmitter<void>();

    get chips(): SummaryChip[] {
        const i = this.inputs;
        if (!i) {
            return [];
        }
        if (this.tabKind === 'select') {
            return this.selectChips(i as SelectInputs);
        }
        if (this.tabKind === 'adjust') {
            return this.adjustChips(i as AdjustInputs);
        }
        return this.closeChips(i as CloseInputs);
    }

    private anyKey(prefixes: string[]): boolean {
        if (!this.changedKeys) {
            return false;
        }
        for (const k in this.changedKeys) {
            if (!this.changedKeys.hasOwnProperty(k)) {
                continue;
            }
            for (let i = 0; i < prefixes.length; i++) {
                if (k.indexOf(prefixes[i]) === 0) {
                    return true;
                }
            }
        }
        return false;
    }

    // Display-name list for the Close "Specific" target, surfaced as the panel tooltip (memory:
    // ui-display-names-not-raw-tickers). Empty for other tabs / the "All" target.
    get hedgeNamesTitle(): string {
        const i = this.inputs;
        if (!i || this.tabKind !== 'close') {
            return '';
        }
        const close = i as CloseInputs;
        if (close.target.kind !== 'hedges') {
            return '';
        }
        const byId: { [id: string]: string } = {};
        (this.bookHedges || []).forEach(h => { byId[h.id] = h.label; });
        return close.target.hedgeIds.map(id => byId[id] || id).join('\n');
    }

    onClick(): void {
        this.expand.emit();
    }

    private selectChips(i: SelectInputs): SummaryChip[] {
        const date = i.date.kind === 'single'
            ? 'Date · ' + fmtDate(i.date.date)
            : 'Dates · ' + fmtDate(i.date.start) + ' → ' + fmtDate(i.date.end);
        // Legacy path (the Select tab renders the embedded OPG copilot today) — no diff keys map here.
        return [chip(date, false), chip(priceRangeChip(i.priceRange), false),
            chip(minProfitChip(i.minProfit, i.minProfitNormalized), false), chip(maxRiskChip(i.maxRisk), false)];
    }

    // The change-key families per chip (CR-20260831-004). The FORM chip owns the shape + strike
    // story (kind, pair, expected-range flag, FT enables/strikes/close-remaining); the Max Risk
    // chip also carries riskSide/halfRisk — those two controls have no chip of their own and live
    // in the Max Risk section on the form.
    private adjustChips(i: AdjustInputs): SummaryChip[] {
        const formChanged = this.anyKey(['priceRange.', 'startStrike', 'endStrike', 'useExpectedRange',
            'ft.up.enabled', 'ft.up.startStrike', 'ft.down.enabled', 'ft.down.startStrike', 'ft.closeRemaining']);
        const riskChanged = this.anyKey(['maxRisk.', 'riskSide', 'halfRisk']);

        // Expiration leads (CR-20260814-001): it names the P&L line everything after it describes, so
        // reading the collapsed panel in order gives which line, then what shape, then what limits.
        const chips = [chip('Expiration · ' + fmtDate(i.expiration), this.anyKey(['expiration'])),
            chip(priceRangeChip(i.priceRange), formChanged)];

        // THE TWO-SIDED FOLLOW-THROUGH CARRIES ITS MONEY PER SIDE (CR-20260824-015): the
        // run-level minimum is 0 by construction there (the global control is hidden), so the
        // global chip would print a $0 nobody typed. Each enabled side gets its own chip, arrowed
        // like the strikes in the form chip above. Harvest rides per side the same way.
        if (i.priceRange && i.priceRange.kind === 'followThrough') {
            if (i.priceRange.up != null) {
                chips.push(chip('Min Profit ↑ · ' + fmtMoney(i.priceRange.up.minProfit)
                    + (i.priceRange.up.minProfitNormalized ? ' · Normalized' : ''),
                    this.anyKey(['ft.up.minProfit'])));
                if (i.priceRange.up.harvest) {
                    chips.push(chip('Harvest Peaks ↑', this.anyKey(['ft.up.harvest'])));
                }
            }
            if (i.priceRange.down != null) {
                chips.push(chip('Min Profit ↓ · ' + fmtMoney(i.priceRange.down.minProfit)
                    + (i.priceRange.down.minProfitNormalized ? ' · Normalized' : ''),
                    this.anyKey(['ft.down.minProfit'])));
                if (i.priceRange.down.harvest) {
                    chips.push(chip('Harvest Peaks ↓', this.anyKey(['ft.down.harvest'])));
                }
            }
            chips.push(chip(maxRiskChip(i.maxRisk), riskChanged));
            return chips;
        }

        chips.push(chip(minProfitChip(i.minProfit, i.minProfitNormalized), this.anyKey(['minProfit.'])),
            chip(maxRiskChip(i.maxRisk), riskChanged));
        // Present only when ON — same convention as the "· normalized" suffixes above. OFF is the
        // default, so its absence already says so, and a permanent "Harvest · off" chip would just
        // be noise on every collapsed panel.
        if (i.harvest === true) {
            chips.push(chip('Harvest Peaks', this.anyKey(['harvest'])));
        }
        return chips;
    }

    private closeChips(i: CloseInputs): SummaryChip[] {
        const dates = i.dates || [];
        const datesChip = dates.length <= 3
            ? 'Dates · ' + dates.map(fmtDate).join(', ')
            : 'Dates · ' + dates.length + ' Selected';
        const targetChip = i.target.kind === 'all'
            ? 'Target · All Hedges'
            : 'Target · Specific (' + i.target.hedgeIds.length + ')';
        return [chip(datesChip, this.anyKey(['dates'])), chip(targetChip, this.anyKey(['target.']))];
    }
}

// One rendered chip of the collapsed summary (CR-20260831-004: chips became objects so they can
// carry the changed-vs-run flag; the text pipeline above is unchanged).
export interface SummaryChip {
    text: string;
    changed: boolean;
}

function chip(text: string, changed: boolean): SummaryChip {
    return { text: text, changed: changed };
}
